import { defineComponent as _defineComponent } from 'vue'
import { renderSlot as _renderSlot, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, resolveDynamicComponent as _resolveDynamicComponent, normalizeProps as _normalizeProps, guardReactiveProps as _guardReactiveProps, createBlock as _createBlock, mergeProps as _mergeProps, createCommentVNode as _createCommentVNode, unref as _unref, withCtx as _withCtx } from "vue"

import { ref, provide, markRaw, computed, nextTick, readonly } from 'vue'
import { NDrawer } from 'naive-ui'
import { drawerInjectionKey } from './context'
import type { ShowOptions, DrawerProvider, optionsListItemType } from './types'


export default /*#__PURE__*/_defineComponent({
  __name: 'Main',
  setup(__props) {

const optionsList = ref<optionsListItemType[]>([])

// 传入prams打开抽屉
const show = (params: ShowOptions) => {
  const {
    component,
    keepAlive,
    aliveKey,
    componentProps,
    isNested,
    componentKey
  } = params
  // 设置keepAlive缓存为true时
  // 根据aliveKey获取缓存的抽屉
  if (keepAlive && aliveKey) {
    const item = optionsList.value.find(item => item.drawerKey === aliveKey)
    if (item) {
      // 是否关闭上一个
      if (!isNested) {
        close(-1)
      }
      // 更新props，缓存中拿到的抽屉visible设为true
      componentProps && (item.componentProps = componentProps)
      item.visible = true
      item.alreadyClose = false
      item.forceClose = false
      return {
        drawerKey: item.drawerKey
      }
    }
  }
  let drawerKey = componentKey || component?.name || 'common'
  // 是否有重复的drawerKey
  const isDuplicateKey = optionsList.value.find(
    item => item.drawerKey === drawerKey
  )
  if (isDuplicateKey) {
    drawerKey = `${drawerKey}-renew`
  }
  optionsList.value.push({
    ...params,
    component: markRaw(component),
    visible: false,
    alreadyClose: false,
    forceClose: false,
    drawerKey
  })
  nextTick(() => {
    // 默认关闭前一个打开现在push进来的抽屉，isNested设为true则不会关闭嵌套打开的上一个抽屉
    // 不嵌套展示，则关闭上一个抽屉
    if (!isNested) {
      close(-2, -1)
    }
    const lastItem = optionsList.value.slice(-1)[0]
    lastItem.visible = true
  })

  return { drawerKey: drawerKey }
}

// 将关闭的抽屉从列表中去除
const afterClosed = (options: optionsListItemType) => {
  if (options.keepAlive && !options.forceClose) {
    return
  }
  const index = optionsList.value.findIndex(
    item => item.drawerKey === options.drawerKey
  )
  if (index !== -1) {
    optionsList.value.splice(index, 1)
  }
  // 关闭抽屉后的回调函数
  options.drawerProps?.onAfterLeave?.()
}

// 关闭指定位置的抽屉，默认关闭当前位置
const close = (starIdx = -1, endIdx?: number) => {
  // 获取当前所有未关闭的抽屉
  const currentItems = optionsList.value.filter(item => !item.alreadyClose)
  if (starIdx < 0) {
    starIdx = starIdx + currentItems.length
  }
  if (endIdx === undefined) {
    endIdx = currentItems.length
  }
  if (endIdx < 0) {
    endIdx = endIdx + currentItems.length
  }
  // 获取需要关闭的抽屉
  const closeItems = currentItems.slice(starIdx, endIdx)
  closeItems.forEach(item => {
    // 关闭时的回调
    item.onClose?.()
    item.visible = false
    item.alreadyClose = true
  })
  return { closeItems }
}

// 通过drawerKey关闭对应抽屉
// 使用改方法需要有componentKey || component?.name
const closeByDrawerKey = (drawerKey: string) => {
  const currentItems = optionsList.value.filter(item => !item.alreadyClose)
  const index = currentItems.findIndex(item => item.drawerKey === drawerKey)
  if (index !== -1) {
    const closeItems = currentItems.slice(index, index + 1)
    closeItems[0].onClose?.()
    closeItems[0].visible = false
    closeItems[0].alreadyClose = true
    return { closeItems }
  } else {
    console.log(drawerKey, '-该drawerKey对应的抽屉不存在')
  }
}

// 强制删除且关闭 keepAlive 的抽屉
const forceClose = (startIndex = -1, endIndex?: number) => {
  const { closeItems } = close(startIndex, endIndex)
  // forceClose设为true则会在关闭抽屉后的回调中删除对应抽屉
  closeItems.forEach(item => {
    item.forceClose = true
  })
  return { closeItems }
}

const forceCloseByDrawerKey = (drawerKey: string) => {
  const result = closeByDrawerKey(drawerKey)
  // forceClose设为true则会在关闭抽屉后的回调中删除对应抽屉
  result?.closeItems?.forEach(item => {
    item.forceClose = true
  })
}

// 关闭全部抽屉
const closeAll = () => {
  optionsList.value.forEach(item => {
    item.visible = false
    item.alreadyClose = true
  })
}

// 强制关闭全部抽屉
const forceCloseAll = () => {
  optionsList.value.forEach(item => {
    item.visible = false
    item.alreadyClose = true
    item.forceClose = true
  })
}

const provider: DrawerProvider = {
  visible: readonly(
    computed(() => optionsList.value.some(item => item.visible))
  ),
  optionsList: readonly(optionsList),
  show,
  close,
  closeByDrawerKey,
  forceClose,
  forceCloseByDrawerKey,
  closeAll,
  forceCloseAll
}

provide(drawerInjectionKey, provider)

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _renderSlot(_ctx.$slots, "default"),
    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(optionsList.value, (options) => {
      return (_openBlock(), _createBlock(_unref(NDrawer), _mergeProps({
        key: options.drawerKey,
        show: options.visible,
        "onUpdate:show": ($event: any) => ((options.visible) = $event),
        onAfterLeave: ($event: any) => (afterClosed(options))
      }, options.drawerProps, {
        class: options?.className
      }), {
        default: _withCtx(() => [
          (options?.component)
            ? (_openBlock(), _createBlock(_resolveDynamicComponent(options?.component), _normalizeProps(_mergeProps({ key: 0 }, options?.componentProps)), null, 16))
            : _createCommentVNode("", true)
        ]),
        _: 2
      }, 1040, ["show", "onUpdate:show", "onAfterLeave", "class"]))
    }), 128))
  ], 64))
}
}

})